import { ChangeEvent, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, UploadPrefix } from 'entities';

import { Row, IconButton, LinearProgress } from 'components/Blocks';
import { CloudUploadIcon, XIcon } from 'components/Icons';

import { useUser } from 'hooks';

import { getID } from 'utils/id';

import { cn } from 'styles';
import { UploadedFile } from 'types';

interface SingleFileUploadButtonProps {
  file: UploadedFile | null;
  setFile: (file: UploadedFile | null) => void;
  uploadPrefix: UploadPrefix;
  acceptedFormats?: string[];
  allowRemoval?: boolean;
  disabled?: boolean;
  className?: string;
}

function fixName(name: string): string {
  const [extension, ...parts] = name.split('.').reverse();

  const trimmed = parts.join('.').slice(0, 24);

  return `${trimmed}.${extension}`;
}

export function SingleFileUploadButton({
  file,
  setFile,
  uploadPrefix,
  allowRemoval = true,
  acceptedFormats,
  disabled,
  className,
}: SingleFileUploadButtonProps) {
  const user = useUser();
  const { t } = useLang();

  const [percentage, setPercentage] = useState(0);

  const id = useMemo(() => getID(), []);

  const upload = actions.uploads.uploadFile.use();

  async function executeUpload(file: File): Promise<void> {
    const result = await upload.execute({
      file,
      prefix: uploadPrefix,
      onUploadProgress: ({ loaded, total }) => {
        setPercentage((loaded / (total || 100000000)) * 100);
      },
    });

    setPercentage(0);

    if (!result) return;

    setFile({
      id,
      name: file.name,
      url: result.url,
      uploadedAt: new Date().toISOString(),
      uploadedBy: user.id,
    });
  }

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    const [file] = target.files || [];

    if (!file) return;

    const formatOk = acceptedFormats?.length ? acceptedFormats.includes(file.type) : true;

    if (!formatOk) return;

    executeUpload(file);
  };

  if (upload.loading) return <LinearProgress value={percentage} className="mt-2" />;

  if (file)
    return (
      <Row gap="2">
        <p className="text-sm italic opacity-80">{fixName(file.name)}</p>

        {allowRemoval && (
          <IconButton onClick={() => setFile(null)}>
            <XIcon size={14} />
          </IconButton>
        )}
      </Row>
    );

  return (
    <label
      htmlFor={id}
      className={cn(
        'cursor-pointer rounded-md border px-3 py-2 transition-all',
        disabled ? 'cursor-default opacity-50' : 'hover:bg-hover',
        className,
      )}
    >
      <Row className="min-w-full" justifyContent="center" gap="2.5">
        <CloudUploadIcon color="primary" />

        <span className="text-xs text-foreground">{t('upload-a-file-here')}</span>
      </Row>

      <input
        className="hidden"
        disabled={disabled}
        type="file"
        id={id}
        multiple={false}
        onChange={handleChange}
      />
    </label>
  );
}
